/**
 * LinesSection 訂單明細 (S2.3) — read-only persisted lines with the trace
 * affordance on every priced line (PriceTraceTrigger → shared PriceTracePanel
 * over the IMMUTABLE persisted priceTrace snapshot — the anti-175% artifact,
 * S9.2 swap), per-line 預估毛利
 * (Board D profit), and the 關聯採購單 linked-PO chips rail (chips render
 * from linkedPoIds; the per-line rail populates with W3 FulfillmentLine).
 * Modification is NOT inline — the separate modify flow owns it
 * (SO-PO board: 一條建立、一條修改).
 */

import { Table, Tag, Typography, theme } from 'antd';
import type { TableProps } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PurpleTag from '@/components/common/PurpleTag';
import TableContainer, { renderDash } from '@/components/common/TableContainer';
import { SectionHeader } from '@/components/products/ProductDetailPage/sections/SectionHeader';
import { evaluateMarginFloor, lineMarginComparable, roundHalfUp2 } from '@/lib/pricing';
import type { SalesOrder, SalesOrderLine } from '@/types/api/salesOrder';
import { SO_PRICE_DEVIATION_THRESHOLD_PCT } from '@/types/api/salesOrder';
import PriceTraceTrigger from '../../components/PriceTraceTrigger';
import { formatAmount, formatPct } from '../../components/soFormat';
import { DismissibleTooltip } from '../../components/DismissibleTooltip';

const { Text } = Typography;

interface LinesSectionProps {
  so: SalesOrder;
}

/** True when the transacted price deviates beyond the pending-approval gate. */
function lineBeyondThreshold(line: SalesOrderLine): boolean {
  if (line.resolvedUnitPrice === null || line.resolvedUnitPrice === 0) return false;
  const pct = roundHalfUp2(
    (Math.abs(line.unitPrice - line.resolvedUnitPrice) / line.resolvedUnitPrice) * 100
  );
  return pct > SO_PRICE_DEVIATION_THRESHOLD_PCT;
}

export const LinesSection = ({ so }: LinesSectionProps) => {
  const { t } = useTranslation(['orders', 'common']);
  const { token } = theme.useToken();
  const navigate = useNavigate();

  // Minimum margin (2026-09-15): judged against the floor SNAPSHOTTED on this
  // order (`so.marginFloorPct`), never today's company setting — a floor
  // raised after the fact must not retro-tag a saved line. null ⇒ nothing.
  /**
   * Whether this line's margin may be read at all: the price is in the ORDER's
   * currency and the cost build-up in its own, and a difference between two
   * currencies is not a number (2026-09-16).
   */
  const lineComparable = (line: SalesOrderLine) =>
    lineMarginComparable({
      landedCost: line.landedCost,
      currency: so.currency,
      priceTrace: line.priceTrace,
    });

  const lineFloor = (line: SalesOrderLine) =>
    evaluateMarginFloor({
      unitPrice: line.unitPrice,
      landedCost: line.landedCost,
      floorPct: so.marginFloorPct,
      comparable: lineMarginComparable({
        landedCost: line.landedCost,
        // The HEADER currency, as the route decided it (a base-mode line's own
        // currency always equals its snapshot's, so it can never refuse).
        currency: so.currency,
        priceTrace: line.priceTrace,
      }),
    });
  // While the hold is pending the tag is the policy signal (orange); once a
  // manager has released it the fact stays true but is no longer a status —
  // rendered neutrally, never as a stale warning.
  const released = so.priceStatus !== 'pending_approval';

  const columns: TableProps<SalesOrderLine>['columns'] = [
    {
      title: t('orders:fields.product'),
      dataIndex: 'sku',
      key: 'product',
      width: 260,
      render: (_: string, line) => (
        <div className="tw-flex tw-flex-col tw-gap-0.5">
          <span style={{ fontWeight: token.fontWeightStrong }}>{line.sku}</span>
          <span style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}>
            {line.productName}
          </span>
        </div>
      ),
    },
    {
      title: t('orders:so.lines.customerPn'),
      dataIndex: 'customerPartNumber',
      key: 'customerPartNumber',
      width: 140,
      render: (value: string | null | undefined) =>
        value ? <PurpleTag>{value}</PurpleTag> : renderDash(),
    },
    {
      title: t('orders:fields.qty'),
      dataIndex: 'qty',
      key: 'qty',
      width: 90,
      align: 'right',
      render: (qty: number) => <Text style={{ fontVariantNumeric: 'tabular-nums' }}>{qty}</Text>,
    },
    {
      title: t('orders:fields.unitPrice'),
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      width: 170,
      render: (_: number, line) => (
        <div className="tw-flex tw-flex-col tw-gap-1 tw-items-start">
          {line.priceTrace ? (
            <PriceTraceTrigger
              resolution={line.priceTrace}
              unitPrice={line.unitPrice}
              currency={line.currency}
              overrideReason={line.overrideReason}
              snapshot
              marginFloorPct={so.marginFloorPct}
              documentCurrency={so.currency}
            />
          ) : (
            <Text style={{ fontVariantNumeric: 'tabular-nums' }}>
              {formatAmount(line.unitPrice)}
            </Text>
          )}
          {lineBeyondThreshold(line) && (
            <Tag color="orange" style={{ margin: 0 }}>
              {t('orders:so.priceStatus.pending_approval')}
            </Tag>
          )}
        </div>
      ),
    },
    {
      title: t('orders:so.detail.lines.discount'),
      dataIndex: 'discountPct',
      key: 'discountPct',
      width: 100,
      align: 'right',
      render: (value: number | undefined) =>
        value ? (
          <Text style={{ fontVariantNumeric: 'tabular-nums' }}>−{formatAmount(value)}%</Text>
        ) : (
          renderDash()
        ),
    },
    {
      title: t('orders:so.lines.lineTotal'),
      dataIndex: 'lineTotal',
      key: 'lineTotal',
      width: 130,
      align: 'right',
      render: (value: number) => (
        <Text style={{ fontVariantNumeric: 'tabular-nums' }}>{formatAmount(value)}</Text>
      ),
    },
    {
      title: t('orders:so.lines.margin'),
      key: 'margin',
      width: 150,
      align: 'right',
      // Minimum margin (2026-09-15): the tag sits in the MARGIN column — its
      // referent is the margin, not the price — and is judged against the
      // floor snapshotted on this order, never today's setting.
      render: (_: unknown, line) =>
        line.estMargin !== null && lineComparable(line) ? (
          <div className="tw-flex tw-flex-col tw-gap-1 tw-items-end">
            <Text
              style={{
                fontVariantNumeric: 'tabular-nums',
                color: line.estMargin >= 0 ? token.colorSuccess : token.colorError,
              }}
            >
              {formatAmount(line.estMargin)}
              {line.estMarginPct !== null ? ` (${formatAmount(line.estMarginPct)}%)` : ''}
            </Text>
            {(() => {
              const floor = lineFloor(line);
              if (floor.status !== 'below_floor') return null;
              return (
                <DismissibleTooltip
                  title={t(
                    // A SAVED order: pending reads "is in price review", never
                    // the draft's "saving puts…" (2026-09-16 copy fix).
                    released
                      ? 'orders:so.lines.belowFloorReleasedTooltip'
                      : 'orders:so.lines.belowFloorPendingTooltip',
                    {
                      margin: formatPct(floor.marginPct),
                      floor: formatPct(floor.floorPct),
                      markup: floor.markupPct === null ? '—' : formatPct(floor.markupPct),
                    }
                  )}
                >
                  <Tag color={released ? undefined : 'orange'} style={{ margin: 0 }} tabIndex={0}>
                    {t(released ? 'orders:so.lines.belowFloorReleased' : 'orders:so.lines.belowFloor')}
                  </Tag>
                </DismissibleTooltip>
              );
            })()}
          </div>
        ) : line.landedCost !== null && !lineComparable(line) ? (
          <DismissibleTooltip
            title={t('orders:so.lines.marginNotComparable', {
              orderCurrency: so.currency,
              costCurrency: line.priceTrace?.currency ?? '—',
            })}
          >
            <span tabIndex={0}>{renderDash()}</span>
          </DismissibleTooltip>
        ) : (
          renderDash()
        ),
    },
  ];

  return (
    <div>
      <SectionHeader
        title={t('orders:so.detail.sections.lines')}
        subtitle={t('orders:so.detail.lines.subtitle')}
      />
      <TableContainer>
        <Table<SalesOrderLine>
          rowKey="id"
          dataSource={so.lines}
          columns={columns}
          pagination={false}
          scroll={{ x: 1000 }}
        />
      </TableContainer>

      {/* 關聯採購單 linked-PO chips rail (populated by W3 SO→PO Allocations) */}
      <div className="tw-mt-6">
        <Text strong style={{ display: 'block', marginBottom: token.marginXS }}>
          {t('orders:so.detail.linkedPo.title')}
        </Text>
        {so.linkedPoIds && so.linkedPoIds.length > 0 ? (
          <div className="tw-flex tw-gap-2 tw-flex-wrap">
            {so.linkedPoIds.map((poId) => (
              <Tag
                key={poId}
                color="geekblue"
                style={{ margin: 0, cursor: 'pointer' }}
                onClick={() => navigate(`/auth/purchase-orders/${poId}`)}
              >
                {poId}
              </Tag>
            ))}
          </div>
        ) : (
          <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
            {t('orders:so.detail.linkedPo.empty')}
          </Text>
        )}
      </div>
    </div>
  );
};

export default LinesSection;
